import { STYLES, StyleKey } from '../../data/styles';
import { Scores } from '../../types';

interface StyleCardProps {
  styleKey: StyleKey;
  isPrimary: boolean;
  scores: Scores;
}

export function StyleCard({ styleKey, isPrimary, scores }: StyleCardProps) {
  const style = STYLES[styleKey];
  const total = Object.values(scores).reduce((a, b) => a + b, 0);

  return (
    <div
      className={`rounded-xl overflow-hidden border-2 ${isPrimary ? 'shadow-lg' : 'shadow-sm'}`}
      style={{ borderColor: style.color }}
    >
      {/* Header */}
      <div
        className="px-6 py-5 flex items-center justify-between"
        style={{ backgroundColor: style.color }}
      >
        <div>
          <p className="text-white/80 text-xs font-semibold uppercase tracking-widest mb-0.5">
            {isPrimary ? 'Dominant Style' : 'Backup Style'}
          </p>
          <h3 className="text-white font-bold text-2xl">{style.name}</h3>
          <p className="text-white/90 text-sm font-medium">{style.nickname}</p>
        </div>
        <div className="text-white/20 text-6xl font-black leading-none select-none">
          {scores[styleKey]}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 bg-white">
        <p className="text-brand/80 text-sm leading-relaxed mb-5">{style.summary}</p>

        {isPrimary && (
          <>
            {/* Traits */}
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-slate mb-2">
                Key traits
              </p>
              <div className="flex flex-wrap gap-2">
                {style.traits.map((t) => (
                  <span
                    key={t}
                    className="text-xs px-2.5 py-1 rounded-full font-medium"
                    style={{ backgroundColor: style.lightColor, color: style.darkColor }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Perceptions */}
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-slate mb-2">
                  Seen positively as
                </p>
                <ul className="text-sm text-brand/80 space-y-1">
                  {style.positivePerceptions.map((p) => (
                    <li key={p} className="flex items-start gap-1.5">
                      <span className="mt-0.5 text-green-500 flex-shrink-0">✓</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-slate mb-2">
                  Under stress seen as
                </p>
                <ul className="text-sm text-brand/80 space-y-1">
                  {style.stressPerceptions.map((p) => (
                    <li key={p} className="flex items-start gap-1.5">
                      <span className="mt-0.5 text-amber-500 flex-shrink-0">!</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* High wants */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-slate mb-2">
                Motivated by
              </p>
              <p className="text-sm text-brand/80">{style.highWants.join(' · ')}</p>
            </div>
          </>
        )}

        {/* Score bar */}
        <div className="mt-5 pt-4 border-t border-cream/30">
          <div className="flex justify-between text-xs text-brand-slate mb-1.5">
            <span>Score</span>
            <span>
              {scores[styleKey]} / {total}
            </span>
          </div>
          <div className="w-full bg-cream/20 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${(scores[styleKey] / total) * 100}%`,
                backgroundColor: style.color,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
